import * as types from "@/redux/ActionTypes";
import authenticationClient from "@/apis/authenticationClient";
import TagManager from "@/gtm/TagManager";
import GoogleTagManager from "@/constants/GoogleTagManager";
import { LoginInfo, PlatformSubscription } from "@/types/LoginInfo";
// Window globals (TIMEZONE_USER_SETTING, USER_ID, etc.) are defined in src/types/global.d.ts

// Extended LoginInfo with additional computed properties
interface LoginInfoWithSubscription extends LoginInfo {
  organisation?: {
    id?: number;
    name?: string;
    timezone?: string;
    platformSubscription?: PlatformSubscription;
    numberOfActiveSubscriptions?: number;
    organisationClassification?: string;
    organizationMainAssetType?: string;
    organizationCountry?: string;
    organizationRegion?: string;
    organizationSegment?: string;
  };
}

// Action interfaces
interface LoginSuccessAction {
  type: typeof types.LOG_IN_SUCCESS;
  data: LoginInfoWithSubscription;
}

interface UpdateProfileAction {
  type: typeof types.UPDATE_PROFILE;
  data: Partial<LoginInfo>;
}

interface LogoutAction {
  type: typeof types.LOG_OUT;
  callBack?: () => void;
}

interface DefaultAction {
  type: string;
  data?: unknown;
}

type UserAction = LoginSuccessAction | UpdateProfileAction | LogoutAction | DefaultAction;

export type UserState = LoginInfo;

const initialState: UserState = {};

const myReducer = (state: UserState = initialState, action: UserAction): UserState => {
  let loginInfo = (action as LoginSuccessAction | UpdateProfileAction).data as LoginInfoWithSubscription;

  switch (action.type) {
    case types.LOG_IN_SUCCESS:
      //old structure needs use global var

      // eslint-disable-next-line no-case-declarations
      let timezoneUserSetting: string;
      if (loginInfo.timezone) {
        timezoneUserSetting = loginInfo.timezone;
      } else if (loginInfo.organisation && loginInfo.organisation.timezone) {
        timezoneUserSetting = loginInfo.organisation.timezone;
      } else {
        // Should never happen because the organisation's timezone cannot be null
        // Just to prevent the application crash
        timezoneUserSetting = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }
      window.TIMEZONE_USER_SETTING = timezoneUserSetting;

      // Locale#week.dow should be an integer representing the first day of the week, 0 is Sunday, 1 is Monday, ..., 6 is Saturday.
      // @see: https://momentjscom.readthedocs.io/en/latest/moment/07-customization/16-dow-doy/
      // The new one from STIC-4133:
      loginInfo.weekStart = 1;
      if (loginInfo.firstDayOfWeek === "SUNDAY") {
        loginInfo.weekStart = 0;
      }

      window.loginInfo = loginInfo;
      window.USER_ID = loginInfo.userId;
      window.GLOBAL_PERMISSIONS = {};
      // eslint-disable-next-line no-case-declarations
      const permissions = loginInfo.userPermissions || [];
      for (let i = 0; i < permissions.length; i++) {
        window.GLOBAL_PERMISSIONS[permissions[i]] = true;
      }
      loginInfo.permissionsMap = window.GLOBAL_PERMISSIONS;

      window.FEATURES_PARAMETER = loginInfo.featureParameters || {};

      sessionStorage.setItem("SNT-VERSION", loginInfo.sntVersion || "");

      // eslint-disable-next-line no-case-declarations
      const assetLocationState = sessionStorage.getItem("ASSET_LOCATION");
      if (assetLocationState) {
        const pageSetting = JSON.parse(assetLocationState) as Record<string, unknown> | null;
        if (pageSetting) {
          delete pageSetting.currentBounds;
          sessionStorage.setItem("ASSET_LOCATION", JSON.stringify(pageSetting));
        }
      }

      // eslint-disable-next-line no-case-declarations
      const VITE_GTM_ID = GoogleTagManager.VITE_GTM_ID,
        NO_VALUE = GoogleTagManager.NO_VALUE,
        hostName = window.location.hostname;

      if (
        sessionStorage.getItem(VITE_GTM_ID) &&
        sessionStorage.getItem(VITE_GTM_ID) !== NO_VALUE &&
        hostName !== "localhost"
      ) {
        TagManager.dataLayer({
          dataLayer: {
            userId: loginInfo.userId,
            userLanguage: loginInfo.language,
            orgId: loginInfo.organisation?.id,
            orgName: loginInfo.organisation?.name,
            plan: loginInfo.organisation?.platformSubscription?.basePlanKey,
            numberOfActiveSubscriptions:
              loginInfo.organisation?.numberOfActiveSubscriptions,
            organisationClassification:
              loginInfo.organisation?.organisationClassification,
            organizationMainAssetType:
              loginInfo.organisation?.organizationMainAssetType,
            organizationCountry: loginInfo.organisation?.organizationCountry,
            organizationRegion: loginInfo.organisation?.organizationRegion,
            organizationSegment: loginInfo.organisation?.organizationSegment,
          },
          nonce: loginInfo.nonce,
        });
      }

      return { ...(action as LoginSuccessAction).data };
    case types.UPDATE_PROFILE:
      loginInfo = { ...state, ...(action as UpdateProfileAction).data };

      return loginInfo;
    case types.LOG_OUT:
      authenticationClient
        .logout()
        .then(() => {
          const callBack = (action as LogoutAction).callBack;
          if (callBack) {
            callBack();
          } else {
            window.history.pushState({}, "", "/login");
          }
        })
        .catch((err: unknown) => {
          console.log(err);
        });
      return {};
    default:
      return state;
  }
};

export default myReducer;
